import type { ReactNode } from 'react';
import { Lock } from 'lucide-react';

import { Tile, TileBody, TileHeader } from '../../../../components/shared';
import { Badge, Button, Skeleton } from '../../../../components/ui';
import { useLoi } from '../../../../hooks/useLoi';
import { useT } from '../../../../i18n';
import type { FormCaiDat } from '../../hooks/useFormCaiDat';
import { DongCaiDat } from './DongCaiDat';

/** Một nhóm con trong khối: tiêu đề nhỏ + các khoá của nó. */
export interface NhomKhoa {
  tieuDe: string;
  /** Một câu dưới tiêu đề nhóm, ví dụ ai quản lý nhóm này. */
  mo?: string;
  khoa: readonly string[];
}

const laNhom = (x: string | NhomKhoa): x is NhomKhoa => typeof x !== 'string';

/**
 * Một khối tham số: tiêu đề + các dòng dựng từ danh mục.
 *
 * Nhận **danh sách khoá**, không nhận cấu hình ô: khoá nào không có trong chính
 * sách (tầng này không có, hoặc danh mục chưa khai) thì lặng lẽ bỏ, khối không
 * còn dòng nào thì không vẽ — để thêm một tham số vào danh mục không làm vỡ màn.
 *
 * Khối nhiều tham số truyền `khoa` dạng **nhóm** (`NhomKhoa[]`): mỗi nhóm có
 * tiêu đề nhỏ, nên người đọc quét theo nhóm thay vì một cột dài liền mạch.
 * Nhóm không còn dòng nào thì bỏ cả tiêu đề.
 */
export function KhoiCaiDat({
  form,
  tieuDe,
  mo,
  icon,
  khoa,
  ipHienTai,
  hanhDong,
  duoi,
}: {
  form: FormCaiDat;
  tieuDe: string;
  mo?: string;
  icon?: ReactNode;
  khoa: readonly string[] | readonly NhomKhoa[];
  ipHienTai?: string | null;
  hanhDong?: ReactNode;
  /** Nội dung thêm dưới ô nhập của từng dòng, ví dụ nút "Gửi thử". */
  duoi?: (khoa: string) => ReactNode;
}) {
  const { t } = useT();
  const coMat = (k: string) => form.danhMuc.has(k) && (form.policy?.settings.some((m) => m.key === k) ?? false);
  const nhom: NhomKhoa[] = (khoa as readonly (string | NhomKhoa)[]).some(laNhom)
    ? (khoa as readonly NhomKhoa[]).map((n) => ({ tieuDe: n.tieuDe, mo: n.mo, khoa: n.khoa.filter(coMat) }))
    : [{ tieuDe: '', khoa: (khoa as readonly string[]).filter(coMat) }];
  const nhomCoDong = nhom.filter((n) => n.khoa.length > 0);
  if (form.policy && nhomCoDong.length === 0) return null;
  // Không tải được: dải lỗi phía trên đã nói, đừng để khung chờ quay mãi bên dưới.
  if (!form.policy && form.loiTai) return null;

  const ip = ipHienTai ?? form.policy?.caller_ip ?? null;
  const tatCa = nhomCoDong.flatMap((n) => n.khoa);
  const coNhap = tatCa.some((k) => k in form.nhap);
  const chiXem =
    !!form.policy &&
    (form.chiDoc || tatCa.every((k) => !form.policy?.settings.find((m) => m.key === k)?.editable));
  const nhanKhoi = (coNhap || chiXem || hanhDong) && (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {hanhDong}
      {coNhap && <Badge variant="warning">{t('caiDat.chuaLuu')}</Badge>}
      {chiXem && (
        <Badge variant="neutral">
          <Lock size={11} aria-hidden />
          {t('caiDat.chiXem')}
        </Badge>
      )}
    </div>
  );
  const cacDong = (ds: readonly string[]) => (
    <div className="divide-y divide-default">
      {ds.map((k) => (
        <DongCaiDat key={k} form={form} khoa={k} ipHienTai={ip} phu={duoi?.(k)} />
      ))}
    </div>
  );

  return (
    <Tile>
      <TileHeader icon={icon} tone="primary" title={tieuDe} mo={mo} action={nhanKhoi || undefined} />
      <TileBody className="py-5">
        {form.dangTai || !form.policy ? (
          <div className="space-y-3" aria-busy>
            <Skeleton className="h-16 w-full rounded" />
            <Skeleton className="h-16 w-full rounded" />
          </div>
        ) : nhomCoDong.length === 1 && !nhomCoDong[0]!.tieuDe ? (
          cacDong(nhomCoDong[0]!.khoa)
        ) : (
          <div className="space-y-6">
            {nhomCoDong.map((n) => (
              <section key={n.tieuDe} aria-label={n.tieuDe}>
                <div className="mb-3 border-b border-default pb-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-content-muted">{n.tieuDe}</h3>
                  {n.mo && <p className="mt-1 max-w-prose text-xs leading-relaxed text-content-muted">{n.mo}</p>}
                </div>
                {cacDong(n.khoa)}
              </section>
            ))}
          </div>
        )}
      </TileBody>
    </Tile>
  );
}

/** Dải lỗi khi không tải được chính sách hoặc danh mục. */
export function LoiTaiCaiDat({ form }: { form: FormCaiDat }) {
  const { t } = useT();
  const dichLoi = useLoi();
  return (
    <div
      role="alert"
      className="flex items-center justify-between gap-3 rounded-lg border border-danger-200 bg-danger-50 px-4 py-3"
    >
      <span className="text-sm text-danger-700">
        {t('caiDat.loiTai', { loi: dichLoi(form.loiTai) })}
      </span>
      <Button variant="outline" size="sm" onClick={form.taiLai}>
        {t('chung.thuLai')}
      </Button>
    </div>
  );
}
